import React, { useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  ListItemButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import MicIcon from "@mui/icons-material/Mic";
import SettingsIcon from "@mui/icons-material/Settings";
import { theme } from "./theme";

const StyledAppBar = styled(AppBar)(() => ({
  backgroundColor: theme.colors.white,
  boxShadow: theme.shadows.small,
  color: theme.colors.text,
}));

const MenuItemContainer = styled(ListItemButton)(
  ({ isActive }: { isActive: boolean }) => ({
    borderLeft: isActive
      ? `4px solid ${theme.colors.primary}`
      : "4px solid transparent",
    backgroundColor: isActive ? theme.colors.background : "transparent",
    "&:hover": {
      backgroundColor: theme.colors.background,
    },
  })
);

interface LayoutProps {
  children: React.ReactNode;
  currentTab: number;
  onTabChange: (newValue: number) => void;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  currentTab,
  onTabChange,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuItemClick = (index: number) => {
    onTabChange(index);
    setDrawerOpen(false); // Close drawer after selection
  };

  const menuItems = [
    {
      text: "Record",
      icon: <MicIcon color={currentTab === 0 ? "primary" : "inherit"} />,
      index: 0,
    },
    {
      text: "Settings",
      icon: <SettingsIcon color={currentTab === 1 ? "primary" : "inherit"} />,
      index: 1,
    },
  ];

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <StyledAppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, fontWeight: 600 }}
          >
            Purple Voice Coach
          </Typography>
        </Toolbar>
      </StyledAppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
        <Box sx={{ width: 250 }} role="presentation">
          <Box sx={{ p: 2, backgroundColor: theme.colors.background }}>
            <Typography
              variant="h6"
              sx={{ color: theme.colors.text, fontWeight: 600 }}
            >
              Menu
            </Typography>
          </Box>
          <Divider />
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <MenuItemContainer
                  onClick={() => handleMenuItemClick(item.index)}
                  isActive={currentTab === item.index}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </MenuItemContainer>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box sx={{ flex: 1, overflow: "auto" }}>{children}</Box>
    </Box>
  );
};

export default Layout;
